import {AxiosResponse} from "axios"
import {
    FacilitiesKeysFilterOptions,
    FacilitiesKey,
    NewFacilitiesKey,
    FacilitiesKeyLocksFilterOptions,
    FacilitiesKeyLock,
    FacilitiesKeyCopiesFilterOptions,
    FacilitiesKeyCopy,
    FacilitiesKeyCopyHistoriesFilterOptions,
    FacilitiesKeyCopyHistory,
    NewFacilitiesKeyCopy,
    NewFacilitiesKeyLock,
    UpdateFacilitiesKeyCopy,
    UpdateFacilitiesKeyLock
} from "@/services/models/FacilitiesKey";
import {PagedData} from "@/services/models/shared/PagedData";
import BimsHttpService from "@/services/BimsHttpService";

class FacilitiesKeysService {
    /**
     * @description Returns a paged list of facilities keys
     */
    getFacilitiesKeys(filter: FacilitiesKeysFilterOptions
    ): Promise<PagedData<FacilitiesKey>> {
        return BimsHttpService.get(`/facilitieskeys`, {
            params: filter
        }).then((response: AxiosResponse<PagedData<FacilitiesKey>>) => {
            return response.data as PagedData<FacilitiesKey>;
        });
    }

    /**
     * @description Returns the facilities key with the specified id
     */
    getFacilitiesKey(id: string): Promise<FacilitiesKey> {
      return BimsHttpService.get(`/facilitieskeys/${id}`).then((response: AxiosResponse<FacilitiesKey>) => {
        return response.data as FacilitiesKey;
        });
    }
    
    /**
     * @description Returns a paged list of facilities key locks
     */
    getFacilitiesKeyLocks(filter: FacilitiesKeyLocksFilterOptions): Promise<PagedData<FacilitiesKeyLock>> {
          return BimsHttpService.get(`/facilitieskeys/locks`, {
              params: filter
          }).then((response: AxiosResponse<PagedData<FacilitiesKeyLock>>) => {
              return response.data as PagedData<FacilitiesKeyLock>;
          });
      }

      /**
     * @description Returns a paged list of facilities key locks for a specific key id
     */
    getFacilitiesKeyLocksForKey(facilitiesKeyId: string, filter: FacilitiesKeyLocksFilterOptions
    ): Promise<PagedData<FacilitiesKeyLock>> {
      return BimsHttpService.get(`/facilitieskeys/${facilitiesKeyId}/locks`, {
          params: filter
      }).then((response: AxiosResponse<PagedData<FacilitiesKeyLock>>) => {
          return response.data as PagedData<FacilitiesKeyLock>;
      });
    }
    
    /**
     * @description Returns the facilities key lock with specified id
     */
    getFacilitiesKeyLock(id: string): Promise<FacilitiesKeyLock> {
        return BimsHttpService.get(`/facilitieskeys/locks/${id}`).then((response: AxiosResponse<FacilitiesKeyLock>) => {
            return response.data as FacilitiesKeyLock;
        });
    }
    
    /**
     * @description Returns a paged list of facilities key copies
     */
    getFacilitiesKeyCopies(filter: FacilitiesKeyCopiesFilterOptions): Promise<PagedData<FacilitiesKeyCopy>> {
      return BimsHttpService.get(`/facilitieskeys/copies`, {
          params: filter
      }).then((response: AxiosResponse<PagedData<FacilitiesKeyCopy>>) => {
          return response.data as PagedData<FacilitiesKeyCopy>;
      });
    }

    /**
     * @description Returns a paged list of facilities key copies for a specific key id
     */
    getFacilitiesKeyCopiesForKey(facilitiesKeyId: string, filter: FacilitiesKeyCopiesFilterOptions
    ): Promise<PagedData<FacilitiesKeyCopy>> {
      return BimsHttpService.get(`/facilitieskeys/${facilitiesKeyId}/copies`, {
          params: filter
      }).then((response: AxiosResponse<PagedData<FacilitiesKeyCopy>>) => {
          return response.data as PagedData<FacilitiesKeyCopy>;
      });
    }

    /**
     * @description Returns the last facilities key copy for the key in order
     */
    getLastFacilitiesKeyCopyForKey(facilitiesKeyId: string): Promise<FacilitiesKeyCopy> {
      return BimsHttpService.get(`/facilitieskeys/${facilitiesKeyId}/lastcopy`).then((response) => {
        return response.data as FacilitiesKeyCopy;
      })
    }

    /**
     * @description Returns a paged list of facilities key copy histories
     */
    getFacilitiesKeyCopyHistories(filter: FacilitiesKeyCopyHistoriesFilterOptions): Promise<PagedData<FacilitiesKeyCopyHistory>> {
      return BimsHttpService.get(`/facilitieskeys/copies/history`, {
          params: filter
      }).then((response: AxiosResponse<PagedData<FacilitiesKeyCopyHistory>>) => {
          return response.data as PagedData<FacilitiesKeyCopyHistory>;
      });
    }

    /**
     * @description Returns a paged list of facilities key copy histories for a specific key copy id
     */
    getFacilitiesKeyCopyHistoriesForKeyCopy(facilitiesKeyCopyId: string, filter: FacilitiesKeyCopyHistoriesFilterOptions
    ): Promise<PagedData<FacilitiesKeyCopyHistory>> {
      return BimsHttpService.get(`/facilitieskeys/copies/${facilitiesKeyCopyId}/history`, {
          params: filter
      }).then((response: AxiosResponse<PagedData<FacilitiesKeyCopyHistory>>) => {
          return response.data as PagedData<FacilitiesKeyCopyHistory>;
      });
    }

    /**
     *  @description Adds new facilities key
     */
    addFacilitiesKey(facilitiesKey: NewFacilitiesKey): Promise<string> {
      return BimsHttpService.post(`/facilitieskeys`,
          facilitiesKey
      ).then(response => {
          return response.data
      });
    }

    /**
     *  @description Adds new facilities key locks for the specified key id
     */
    addFacilitiesKeyLocks(facilitiesKeyId: string, facilitiesKeyLocks: Array<NewFacilitiesKeyLock>): Promise<string> {
      return BimsHttpService.post(`/facilitieskeys/${facilitiesKeyId}/locks`,
        facilitiesKeyLocks
      ).then(response => {
          return response.data
      });
    } 

    /**
     *  @description Adds new facilities key copies for the specified key id
     */
    addFacilitiesKeyCopies(facilitiesKeyId: string, facilitiesKeyCopies: Array<NewFacilitiesKeyCopy>): Promise<string> {
      return BimsHttpService.post(`/facilitieskeys/${facilitiesKeyId}/copies`,
        facilitiesKeyCopies
      ).then(response => {
          return response.data
      });
    } 

    /**
     * @description Updates the facilities key copy
     */
    updateFacilitiesKeyCopy(id: string, facilitiesKeyCopy: UpdateFacilitiesKeyCopy): Promise<any> {
      return BimsHttpService.put(`/facilitieskeys/copies/${id}`,
        facilitiesKeyCopy
      )
    }

    /**
     * @description Updates the facilities key lock
     */
    updateFacilitiesKeyLock(id: string, facilitiesKeyLock: UpdateFacilitiesKeyLock): Promise<any> {
      return BimsHttpService.put(`/facilitieskeys/locks/${id}`,
        facilitiesKeyLock
      )
    }

    /**
     * @description Deletes the facilities key
     */
     deleteFacilitiesKey(id: string): Promise<any> {
      return BimsHttpService.delete(`/facilitieskeys/${id}`)
  }

    /**
     * @description Deletes the facilities key copy
     */
    deleteFacilitiesKeyCopy(id: string): Promise<any> {
        return BimsHttpService.delete(`/facilitieskeys/copies/${id}`)
    }

    /**
     * @description Deletes the facilities key lock
     */
    deleteFacilitiesKeyLock(id: string): Promise<any> {
        return BimsHttpService.delete(`/facilitieskeys/locks/${id}`)
    }
}

export default new FacilitiesKeysService();
